<template>
    <div class="session-card" :class="{ 'session-card--current': session.isCurrent }">
        <div class="session-card__row">
            <!-- Device & Browser -->
            <div class="session-card__device">
                <div class="session-card__icons">
                    <template v-if="isMobileApp">
                        <svg class="supsis-logo" viewBox="0 0 24 24" width="20" height="20">
                            <circle cx="12" cy="12" r="11" fill="#1890ff"/>
                            <text x="12" y="17" text-anchor="middle" fill="#fff" font-size="14" font-weight="bold" font-family="Arial">S</text>
                        </svg>
                        <md-icon class="mobile-app-icon">smartphone</md-icon>
                    </template>
                    <template v-else>
                        <DeviceIcon type="browser" :name="detectedClient || session.browser" :size="20" />
                        <DeviceIcon type="os" :name="session.os" :size="16" />
                    </template>
                </div>
                <div class="session-card__device-info">
                    <span class="session-card__browser">{{ browserDisplay }}</span>
                    <span class="session-card__os">{{ osDisplay }}</span>
                </div>
            </div>

            <!-- User Info (for admin view) -->
            <div class="session-card__user" v-if="showUserInfo && session.userId">
                <a-avatar :size="20" icon="user" :src="userAvatar" />
                <span class="session-card__user-name">{{ userName }}</span>
            </div>

            <!-- Location -->
            <div class="session-card__location">
                <md-icon class="location-icon">location_on</md-icon>
                <span>{{ locationDisplay }}</span>
            </div>

            <!-- IP -->
            <div class="session-card__ip">
                <md-icon class="ip-icon">router</md-icon>
                <span>{{ session.ipAddress || "-" }}</span>
            </div>

            <!-- Times -->
            <div class="session-card__times">
                <a-tooltip :title="createdAtTooltip">
                    <span class="session-card__time">
                        <md-icon>event</md-icon> {{ createdAtDisplay }}
                    </span>
                </a-tooltip>
                <a-tooltip :title="lastAccessTooltip">
                    <span class="session-card__time">
                        <md-icon>access_time</md-icon> {{ lastAccessDisplay }}
                    </span>
                </a-tooltip>
            </div>

            <!-- Status Tags -->
            <div class="session-card__status">
                <a-tag v-if="session.isCurrent" color="green" size="small">
                    {{ $t("sessions.currentSession") }}
                </a-tag>
                <a-tag v-if="session.platform === 'mobile-app'" color="blue" size="small">
                    Mobile
                </a-tag>
            </div>

            <!-- Actions -->
            <div class="session-card__actions">
                <a-button
                    v-if="!session.isCurrent"
                    type="danger"
                    size="small"
                    @click="handleRevoke"
                >
                    {{ $t("sessions.revoke") }}
                </a-button>
            </div>
        </div>
    </div>
</template>

<script>
import DeviceIcon from "./DeviceIcon.vue";
import moment from "moment";

export default {
    name: "SessionCard",
    components: {
        DeviceIcon,
    },
    props: {
        session: {
            type: Object,
            required: true,
        },
        showUserInfo: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isMobileApp() {
            return this.session.platform === "mobile-app";
        },
        detectedClient() {
            var ua = (this.session.userAgent || "").toLowerCase();
            if (!ua) return null;
            
            // HTTP Clients
            if (ua.includes("postman")) return "Postman";
            if (ua.includes("insomnia")) return "Insomnia";
            if (ua.includes("axios")) return "Axios";
            if (ua.includes("curl")) return "cURL";
            if (ua.includes("httpie")) return "HTTPie";
            if (ua.includes("wget")) return "wget";
            
            // Programming languages / libraries
            if (ua.includes("python") || ua.includes("requests") || ua.includes("aiohttp") || ua.includes("urllib")) return "Python";
            if (ua.includes("node") || ua.includes("got/") || ua.includes("node-fetch") || ua.includes("superagent")) return "Node.js";
            if (ua.includes("go-http") || ua.includes("golang")) return "Go";
            if (ua.includes("java") || ua.includes("okhttp") || ua.includes("apache-httpclient")) return "Java";
            if (ua.includes("php") || ua.includes("guzzle")) return "PHP";
            if (ua.includes("ruby") || ua.includes("faraday") || ua.includes("httparty")) return "Ruby";
            if (ua.includes(".net") || ua.includes("dotnet") || ua.includes("httpclient")) return ".NET";
            
            // Bots
            if (ua.includes("bot") || ua.includes("crawler") || ua.includes("spider")) return "Bot";
            
            return null;
        },
        browserDisplay() {
            if (this.isMobileApp) {
                return "Supsis Mobile App";
            }
            // Eğer HTTP client tespit edildiyse onu göster
            if (this.detectedClient) {
                return this.detectedClient;
            }
            var browser = this.session.browser || "Unknown";
            var version = this.session.browserVersion || "";
            return version ? browser + " " + version : browser;
        },
        osDisplay() {
            if (this.isMobileApp) {
                var os = this.session.os || "";
                if (os.toLowerCase().includes("ios")) return "iOS";
                if (os.toLowerCase().includes("android")) return "Android";
                return "Mobile";
            }
            // HTTP client ise OS gösterme
            if (this.detectedClient) {
                return "HTTP Client";
            }
            var os = this.session.os || "Unknown";
            var version = this.session.osVersion || "";
            return version ? os + " " + version : os;
        },
        locationDisplay() {
            var geo = this.session.geoLocation || {};
            var parts = [];
            if (geo.city) parts.push(geo.city);
            if (geo.country) parts.push(geo.country);
            return parts.length > 0 ? parts.join(", ") : "-";
        },
        createdAtDisplay() {
            if (!this.session.createdAt) return "-";
            return moment(this.session.createdAt).fromNow();
        },
        createdAtTooltip() {
            if (!this.session.createdAt) return "-";
            var fullDate = moment(this.session.createdAt).format("DD.MM.YYYY HH:mm:ss");
            return this.$t("sessions.createdAt") + ": " + fullDate;
        },
        lastAccessDisplay() {
            if (!this.session.lastAccessAt) return "-";
            return moment(this.session.lastAccessAt).fromNow();
        },
        lastAccessTooltip() {
            if (!this.session.lastAccessAt) return "-";
            var fullDate = moment(this.session.lastAccessAt).format("DD.MM.YYYY HH:mm:ss");
            return this.$t("sessions.lastAccess") + ": " + fullDate;
        },
        userName() {
            if (this.session.userId && typeof this.session.userId === "object") {
                return this.session.userId.name || this.session.userId.email || "-";
            }
            return "-";
        },
        userAvatar() {
            if (this.session.userId && typeof this.session.userId === "object") {
                return this.session.userId.gravatar || null;
            }
            return null;
        },
    },
    methods: {
        handleRevoke() {
            this.$emit("revoke", this.session._id);
        },
    },
};
</script>

<style lang="scss" scoped>
.session-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 10px 16px;
    transition: all 0.2s ease;

    &:hover {
        background: #fafafa;
    }

    &--current {
        border-color: #52c41a;
        background: #f6ffed;

        &:hover {
            background: #f0ffe6;
        }
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }

    &__device {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 180px;
    }

    &__icons {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px;
        background: #f5f5f5;
        border-radius: 6px;

        .supsis-logo {
            display: block;
        }

        .mobile-app-icon {
            font-size: 16px !important;
            color: #1890ff;
        }
    }

    &__device-info {
        display: flex;
        flex-direction: column;
        line-height: 1.3;
    }

    &__browser {
        font-weight: 600;
        font-size: 13px;
        color: #333;
    }

    &__os {
        font-size: 11px;
        color: #888;
    }

    &__user {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 120px;
    }

    &__user-name {
        font-size: 12px;
        color: #333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100px;
    }

    &__location {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #666;
        min-width: 120px;

        .location-icon {
            color: #f5222d;
            font-size: 16px !important;
        }
    }

    &__ip {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #666;
        font-family: monospace;
        min-width: 110px;

        .ip-icon {
            color: #1890ff;
            font-size: 16px !important;
        }
    }

    &__times {
        display: flex;
        gap: 12px;
    }

    &__time {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: #888;
        cursor: default;

        .md-icon {
            font-size: 14px !important;
            margin-right: 4px;
        }
    }

    &__status {
        display: flex;
        gap: 6px;
        flex: 1;

        .ant-tag {
            margin: 0;
            font-size: 11px;
            padding: 0 6px;
            line-height: 18px;
        }
    }

    &__actions {
        display: flex;
        gap: 6px;

        .ant-btn-sm {
            font-size: 12px;
            height: 24px;
            padding: 0 8px;
        }
    }
}
</style>
